import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  ADMIN_DEPARTMENTS,
  ADMIN_ROLES,
  AdminDepartment,
  AdminModule,
  AdminPermissions,
  AdminRole,
  buildPermissionsFromDepartments,
  canPerformFeature,
  cloneModules,
  countModuleAccess,
  DEPARTMENT_LABELS,
  DEPARTMENT_OPTIONS,
  FeatureOverrides,
  MODULE_FEATURES,
  normalizeDepartments,
  ROLE_COLORS,
  ROLE_LABELS,
} from '../../utils/adminPermissions';
import {
  AdminAuditLogEntry,
  AdminStaffMember,
  AdminZoneOption,
  cloneAdminStaffPermissions,
  getAdminStaffAuditLog,
  getAdminStaffList,
  resetAdminStaffPermissions,
  updateAdminStaff,
  updateAdminStaffPermissions,
} from '../../services/admin';
import PermissionMatrix from './PermissionMatrix';
import { useAdminPermission } from '../../hooks/useAdminPermission';
import AdminZoneAutocomplete from './AdminZoneAutocomplete';
import useSnackbar from '../../hooks/useSnackbar';
import withComponentAuth from '../../hoc/withComponentAuth';

const AUDIT_ACTION_LABELS: Record<AdminAuditLogEntry['action'], string> = {
  STAFF_CREATED: 'Account created',
  ROLE_CHANGED: 'Role changed',
  DEPARTMENT_CHANGED: 'Department changed',
  PERMISSIONS_UPDATED: 'Custom permissions updated',
  PERMISSIONS_RESET: 'Permissions reset to role default',
  PERMISSIONS_CLONED: 'Permissions cloned from another user',
  STAFF_ACTIVATED: 'Account enabled',
  STAFF_DEACTIVATED: 'Account disabled',
  STAFF_DELETED: 'Account deleted',
};

const PermissionsScreen = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const { isFounder, canEdit } = useAdminPermission();
  const canEditStaff = canEdit('admin_staff');

  const [staffList, setStaffList] = useState<AdminStaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);

  const [name, setName] = useState('');
  const [role, setRole] = useState<AdminRole>(ADMIN_ROLES.TRAINEE);
  const [departments, setDepartments] = useState<AdminDepartment[]>([ADMIN_DEPARTMENTS.SERVICE]);
  const [location, setLocation] = useState<AdminZoneOption | null>(null);
  const [useCustom, setUseCustom] = useState(false);
  const [modules, setModules] = useState<AdminPermissions['modules']>({
    view: [],
    create: [],
    edit: [],
    delete: [],
  });
  const [saving, setSaving] = useState(false);
  const [featureOverrides, setFeatureOverrides] = useState<FeatureOverrides>({});

  const [auditLog, setAuditLog] = useState<AdminAuditLogEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditLoaded, setAuditLoaded] = useState(false);

  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneSourceId, setCloneSourceId] = useState('');
  const [cloneSearch, setCloneSearch] = useState('');
  const [cloneSaving, setCloneSaving] = useState(false);

  const user = useMemo(() => staffList.find((s) => s._id === id) || null, [staffList, id]);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const res = await getAdminStaffList();
      setStaffList(res?.data || []);
    } catch {
      showSnackbar('Failed to load admin user', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const defaultPermissions = useMemo(
    () => buildPermissionsFromDepartments(role, departments),
    [role, departments],
  );

  useEffect(() => {
    if (!user) return;
    setName(user.name);
    setRole(user.role);
    const depts = normalizeDepartments(user.departments?.length ? user.departments : [user.department], user.department);
    setDepartments(depts);
    setLocation(
      user.location?.name
        ? { _id: user.location.zoneId || user.location.name, name: user.location.name }
        : null,
    );
    setUseCustom(Boolean(user.useCustomPermissions));
    const roleDefaults = buildPermissionsFromDepartments(user.role, depts);
    const source =
      user.useCustomPermissions && user.customPermissions?.modules
        ? user.customPermissions.modules
        : user.resolvedPermissions?.modules || user.defaultPermissions?.modules || roleDefaults.modules;
    setModules(cloneModules(source));
    setFeatureOverrides(user.customPermissions?.features || {});
  }, [user]);

  const handleRoleChange = (nextRole: AdminRole) => {
    setRole(nextRole);
    if (!useCustom) {
      setModules(cloneModules(buildPermissionsFromDepartments(nextRole, departments).modules));
    }
  };

  const handleDepartmentsChange = (next: AdminDepartment[]) => {
    setDepartments(next);
    if (!useCustom) {
      setModules(cloneModules(buildPermissionsFromDepartments(role, next).modules));
    }
  };

  const handleCustomToggle = (enabled: boolean) => {
    setUseCustom(enabled);
    if (enabled && user) {
      const source =
        user.useCustomPermissions && user.customPermissions?.modules
          ? user.customPermissions.modules
          : user.resolvedPermissions?.modules || defaultPermissions.modules;
      setModules(cloneModules(source));
      return;
    }
    setModules(cloneModules(defaultPermissions.modules));
  };

  const handleFeatureModuleCustomizeToggle = (moduleName: AdminModule, enabled: boolean) => {
    setFeatureOverrides((prev) => {
      const next = { ...prev };
      if (!enabled) {
        delete next[moduleName];
        return next;
      }
      const keys = Object.keys(MODULE_FEATURES[moduleName] || {});
      const activePermissions = useCustom
        ? { ...defaultPermissions, modules, useCustomPermissions: true }
        : defaultPermissions;
      next[moduleName] = keys.filter((key) => canPerformFeature(activePermissions, undefined, moduleName, key));
      return next;
    });
  };

  const handleFeatureToggle = (moduleName: AdminModule, featureKey: string) => {
    setFeatureOverrides((prev) => {
      const current = prev[moduleName] || [];
      const next = current.includes(featureKey)
        ? current.filter((k) => k !== featureKey)
        : [...current, featureKey];
      return { ...prev, [moduleName]: next };
    });
  };

  const handleResetToDefault = async () => {
    if (!user) return;
    try {
      setSaving(true);
      const res = await resetAdminStaffPermissions(user._id);
      setUseCustom(false);
      setModules(cloneModules(defaultPermissions.modules));
      setStaffList((prev) => prev.map((s) => (s._id === user._id ? { ...s, ...res.data } : s)));
      showSnackbar('Permissions reset to role default', 'success');
    } catch (error: any) {
      showSnackbar(error?.message || 'Failed to reset permissions', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    try {
      setSaving(true);

      const profileRes = await updateAdminStaff(user._id, {
        name,
        role,
        department: departments[0],
        departments,
        locationZoneId: location?._id,
        locationName: location?.name || '',
      });

      const permRes = await updateAdminStaffPermissions(user._id, {
        useCustomPermissions: useCustom,
        modules: useCustom ? modules : undefined,
        features: featureOverrides,
      });

      setStaffList((prev) =>
        prev.map((s) => (s._id === user._id ? { ...s, ...profileRes.data, ...permRes.data } : s)),
      );
      showSnackbar('User and permissions saved', 'success');
    } catch (error: any) {
      showSnackbar(error?.message || 'Failed to save changes', 'error');
    } finally {
      setSaving(false);
    }
  };

  const fetchAuditLog = async () => {
    if (!id) return;
    try {
      setAuditLoading(true);
      const res = await getAdminStaffAuditLog(id);
      setAuditLog(res?.data || []);
      setAuditLoaded(true);
    } catch {
      showSnackbar('Failed to load activity log', 'error');
    } finally {
      setAuditLoading(false);
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, next: number) => {
    setTab(next);
    if (next === 1 && !auditLoaded) {
      fetchAuditLog();
    }
  };

  const handleClone = async () => {
    if (!user || !cloneSourceId) return;
    try {
      setCloneSaving(true);
      const res = await cloneAdminStaffPermissions(user._id, cloneSourceId);
      setUseCustom(Boolean(res.data.useCustomPermissions));
      setModules(cloneModules(res.data.customPermissions?.modules || modules));
      setStaffList((prev) => prev.map((s) => (s._id === user._id ? { ...s, ...res.data } : s)));
      showSnackbar(res.message || 'Permissions cloned', 'success');
      setCloneOpen(false);
      setCloneSourceId('');
      setCloneSearch('');
    } catch (error: any) {
      showSnackbar(error?.message || 'Failed to clone permissions', 'error');
    } finally {
      setCloneSaving(false);
    }
  };

  const cloneCandidates = staffList.filter(
    (s) =>
      s._id !== id &&
      (s.name?.toLowerCase().includes(cloneSearch.trim().toLowerCase()) ||
        s.email?.toLowerCase().includes(cloneSearch.trim().toLowerCase())),
  );

  const activePermissions = useCustom
    ? { ...defaultPermissions, modules, useCustomPermissions: true }
    : defaultPermissions;

  const getInitials = (value: string) =>
    value.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        {SnackbarComponent}
        <Typography color="#94a3b8">Loading admin user...</Typography>
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        {SnackbarComponent}
        <Typography color="#94a3b8">Admin user not found.</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin-staff')} sx={{ mt: 2 }}>
          Back to Admin Roles
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {SnackbarComponent}

      <Box display="flex" alignItems="center" gap={1.5} mb={2.5}>
        <IconButton onClick={() => navigate('/admin-staff')} sx={{ color: '#94a3b8' }}>
          <ArrowBackIcon />
        </IconButton>
        <Avatar
          sx={{
            width: 44,
            height: 44,
            bgcolor: `${ROLE_COLORS[role]}33`,
            color: ROLE_COLORS[role],
          }}
        >
          {getInitials(user.name || '?')}
        </Avatar>
        <Box flex={1}>
          <Typography variant="h6" fontWeight={600} color="#fff">
            {user.name}
          </Typography>
          <Typography variant="body2" color="#64748b">
            {user.email}
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Chip label={ROLE_LABELS[role]} size="small" sx={{ bgcolor: `${ROLE_COLORS[role]}22`, color: ROLE_COLORS[role] }} />
          <Chip
            label={useCustom ? 'Custom permissions' : 'Role default permissions'}
            size="small"
            variant="outlined"
            sx={{ borderColor: useCustom ? '#f59e0b' : '#333', color: useCustom ? '#f59e0b' : '#94a3b8' }}
          />
          <Chip
            label={`${countModuleAccess(activePermissions)} access grants`}
            size="small"
            variant="outlined"
            sx={{ borderColor: '#333', color: '#64748b' }}
          />
        </Box>
      </Box>

      <Tabs
        value={tab}
        onChange={handleTabChange}
        sx={{
          mb: 2,
          minHeight: 36,
          '& .MuiTab-root': { minHeight: 36, color: '#94a3b8' },
          '& .Mui-selected': { color: '#fff !important' },
        }}
      >
        <Tab label="Details & Permissions" />
        <Tab label="Activity" />
      </Tabs>

      {tab === 0 && (
        <Box display="flex" gap={2} flexDirection={{ xs: 'column', md: 'row' }}>
          <Paper sx={{ p: 2.5, width: { xs: '100%', md: 340 }, bgcolor: '#171717', border: '1px solid #242424', borderRadius: 2, flexShrink: 0 }}>
            <Typography color="#fff" fontWeight={600} mb={2}>
              Details
            </Typography>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
            />
            <TextField
              select
              label="Departments"
              value={departments}
              onChange={(e) => {
                const value = e.target.value;
                const next = (typeof value === 'string' ? value.split(',') : value) as AdminDepartment[];
                handleDepartmentsChange(next.length ? next : departments);
              }}
              fullWidth
              size="small"
              sx={{ mb: 2 }}
              SelectProps={{
                multiple: true,
                renderValue: (selected) => (
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {(selected as AdminDepartment[]).map((dept) => (
                      <Chip key={dept} label={DEPARTMENT_LABELS[dept]} size="small" />
                    ))}
                  </Box>
                ),
              }}
            >
              {DEPARTMENT_OPTIONS.map((d) => (
                <MenuItem key={d} value={d}>
                  <Checkbox size="small" checked={departments.includes(d)} />
                  <ListItemText primary={DEPARTMENT_LABELS[d]} />
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ mb: 2 }}>
              <AdminZoneAutocomplete value={location} onChange={setLocation} showSnackbar={showSnackbar} />
            </Box>
            <TextField
              select
              label="Role"
              value={role}
              onChange={(e) => handleRoleChange(e.target.value as AdminRole)}
              fullWidth
              size="small"
            >
              {Object.values(ADMIN_ROLES).map((r) => (
                <MenuItem key={r} value={r} disabled={r === ADMIN_ROLES.FOUNDER && !isFounder}>
                  {ROLE_LABELS[r]}
                </MenuItem>
              ))}
            </TextField>

            {isFounder && (
              <Button
                startIcon={<ContentCopyIcon />}
                onClick={() => setCloneOpen(true)}
                fullWidth
                sx={{ mt: 2.5, color: '#94a3b8', borderColor: '#333' }}
                variant="outlined"
              >
                Copy permissions from...
              </Button>
            )}
          </Paper>

          <Paper sx={{ p: 2.5, flex: 1, minWidth: 0, bgcolor: '#171717', border: '1px solid #242424', borderRadius: 2, display: 'flex', flexDirection: 'column' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={useCustom}
                    onChange={(e) => handleCustomToggle(e.target.checked)}
                    disabled={!canEditStaff}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#f59e0b' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#f59e0b' },
                    }}
                  />
                }
                label={<Typography variant="body2" color="#e2e8f0">Use custom permissions</Typography>}
              />
              <Button
                size="small"
                startIcon={<RestartAltIcon />}
                onClick={handleResetToDefault}
                disabled={saving || !useCustom || !canEditStaff}
                sx={{ color: '#94a3b8' }}
              >
                Reset to default
              </Button>
            </Box>

            <Typography variant="caption" color="#64748b" mb={1.5} display="block">
              {useCustom
                ? 'Toggle modules below. Orange checkmarks differ from the role default.'
                : 'Showing role default permissions (union of all selected departments for a Trainee). Enable custom to override.'}
            </Typography>

            <Box sx={{ flex: 1, minHeight: 400 }}>
              <PermissionMatrix
                modules={useCustom ? modules : defaultPermissions.modules}
                onChange={setModules}
                readOnly={!useCustom || !canEditStaff}
                compareModules={useCustom ? defaultPermissions.modules : undefined}
                disabledModules={isFounder ? undefined : ['admin_staff']}
              />
            </Box>

            <Divider sx={{ borderColor: '#242424', my: 2 }} />

            <Typography color="#fff" fontWeight={600} mb={0.5}>
              Feature Overrides
            </Typography>
            <Typography variant="caption" color="#64748b" mb={1.5} display="block">
              Grant or deny specific actions inside a module, independent of the matrix above.
              Off = inherits from the module's base permission. Founder-only to edit.
            </Typography>

            {(Object.keys(MODULE_FEATURES) as AdminModule[]).map((moduleName) => {
              const features = MODULE_FEATURES[moduleName] || {};
              const isCustomized = Array.isArray(featureOverrides[moduleName]);
              const activePermissions = useCustom
                ? { ...defaultPermissions, modules, useCustomPermissions: true }
                : defaultPermissions;
              return (
                <Accordion
                  key={moduleName}
                  disableGutters
                  sx={{
                    bgcolor: '#1a1a1a',
                    border: '1px solid #242424',
                    mb: 1,
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#94a3b8' }} />}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" pr={1}>
                      <Typography color="#e2e8f0" fontSize={14} textTransform="capitalize">
                        {moduleName.replace(/_/g, ' ')}
                      </Typography>
                      {isCustomized && (
                        <Chip label="Customized" size="small" sx={{ bgcolor: '#f59e0b22', color: '#f59e0b', height: 22, fontSize: 11 }} />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={isCustomized}
                          disabled={!isFounder}
                          onChange={(e) => handleFeatureModuleCustomizeToggle(moduleName, e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#f59e0b' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#f59e0b' },
                          }}
                        />
                      }
                      label={<Typography variant="caption" color="#94a3b8">Customize this module's features</Typography>}
                      sx={{ mb: 1 }}
                    />
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      {Object.entries(features).map(([featureKey, label]) => {
                        const checked = isCustomized
                          ? (featureOverrides[moduleName] || []).includes(featureKey)
                          : canPerformFeature(activePermissions, undefined, moduleName, featureKey);
                        return (
                          <FormControlLabel
                            key={featureKey}
                            control={
                              <Checkbox
                                size="small"
                                checked={checked}
                                disabled={!isCustomized || !isFounder}
                                onChange={() => handleFeatureToggle(moduleName, featureKey)}
                                sx={{ color: '#475569', '&.Mui-checked': { color: '#3b82f6' }, p: 0.5 }}
                              />
                            }
                            label={<Typography variant="body2" color="#e2e8f0" fontSize={13}>{label}</Typography>}
                          />
                        );
                      })}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}

            <Divider sx={{ borderColor: '#242424', my: 2 }} />

            <Box display="flex" justifyContent="flex-end" gap={1.5}>
              <Button onClick={() => navigate('/admin-staff')} sx={{ color: '#94a3b8' }}>
                Cancel
              </Button>
              {canEditStaff && (
                <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={saving || !name.trim()}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              )}
            </Box>
          </Paper>
        </Box>
      )}

      {tab === 1 && (
        <Paper sx={{ p: 2.5, bgcolor: '#171717', border: '1px solid #242424', borderRadius: 2 }}>
          <Typography color="#fff" fontWeight={600} mb={1.5}>
            Activity
          </Typography>
          {auditLoading ? (
            <Typography color="#64748b" variant="body2">Loading activity...</Typography>
          ) : auditLog.length === 0 ? (
            <Typography color="#64748b" variant="body2">No activity recorded yet.</Typography>
          ) : (
            <List sx={{ p: 0 }}>
              {auditLog.map((entry) => (
                <ListItem
                  key={entry._id}
                  sx={{ borderBottom: '1px solid #242424', px: 0, py: 1.25 }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" color="#e2e8f0">
                        {AUDIT_ACTION_LABELS[entry.action] || entry.action}
                        {entry.actorName ? ` — by ${entry.actorName}` : ''}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="#64748b">
                        {new Date(entry.createdAt).toLocaleString()}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}

      <Dialog
        open={cloneOpen}
        onClose={() => !cloneSaving && setCloneOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#171717', border: '1px solid #242424', color: '#fff' } }}
      >
        <DialogTitle>Copy permissions from...</DialogTitle>
        <DialogContent>
          <TextField
            placeholder="Search by name or email..."
            value={cloneSearch}
            onChange={(e) => setCloneSearch(e.target.value)}
            fullWidth
            size="small"
            sx={{ mb: 1.5 }}
          />
          <List sx={{ maxHeight: 320, overflowY: 'auto' }}>
            {cloneCandidates.map((candidate) => (
              <ListItem
                key={candidate._id}
                onClick={() => setCloneSourceId(candidate._id)}
                sx={{
                  cursor: 'pointer',
                  borderRadius: 1,
                  mb: 0.5,
                  bgcolor: cloneSourceId === candidate._id ? 'rgba(123,31,162,0.12)' : 'transparent',
                  border: cloneSourceId === candidate._id ? '1px solid #7B1FA2' : '1px solid transparent',
                }}
              >
                <ListItemText
                  primary={<Typography color="#e2e8f0">{candidate.name}</Typography>}
                  secondary={<Typography variant="caption" color="#64748b">{candidate.email} — {ROLE_LABELS[candidate.role]}</Typography>}
                />
              </ListItem>
            ))}
            {cloneCandidates.length === 0 && (
              <Typography color="#64748b" variant="body2" sx={{ p: 1 }}>
                No matching admin users.
              </Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCloneOpen(false)} disabled={cloneSaving} sx={{ color: '#94a3b8' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleClone} disabled={cloneSaving || !cloneSourceId}>
            {cloneSaving ? 'Copying...' : 'Copy Permissions'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default withComponentAuth(PermissionsScreen);
